import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

// FIX (Micky cart audit): the storefront serializes concurrent cart writes
// (quantity changes in component-cart-items.js, the free-gift automation in
// free-gift-automation.js) through this shared queue on `window` so Shopify's
// non-transactional AJAX Cart API doesn't race and produce duplicate/lost
// cart lines. This file was previously issuing its /cart/update.js requests
// directly against the network, completely outside that queue - a discount
// apply/remove firing at the same moment as a quantity change or the free
// gift add/remove could interleave with it. Wrapping our own cart writes in
// the same lock (this is an intentional duplicate of the helper in
// component-cart-items.js / free-gift-automation.js, not a shared import,
// matching how those two files already do it) closes that gap.
function withCartLock(fn) {
  const next = (window.__cartMutationQueue || Promise.resolve()).then(fn, fn);
  window.__cartMutationQueue = next.catch(() => {});
  return next;
}

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    // FIX (Micky cart audit): surface a busy state on the submit button while the
    // request is in flight. Previously the button stayed fully clickable during the
    // ~1-2s round trip to /cart/update.js, which invited a second tap on slower
    // connections/devices. That second tap aborted the first request (see
    // #createAbortController below) and the failure was swallowed silently by the
    // empty catch block that used to be here - from the shopper's perspective the
    // "Discount" panel just did nothing. Disabling the button closes that gap.
    const submitButton = form.querySelector('.cart-discount__button');
    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
    }

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await withCartLock(() =>
        fetch(Theme.routes.cart_update_url, {
          ...config,
          signal: abortController.signal,
        })
      );

      if (!response.ok) {
        throw new Error(`Cart update request failed with status ${response.status}`);
      }

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
      // FIX (Micky cart audit): this catch block used to be completely empty, so any
      // failed request (aborted by a double tap, dropped mobile connection, slow
      // network timing out, a blocked/interrupted fetch) produced zero feedback -
      // indistinguishable from the discount panel being broken. We now:
      //  - ignore AbortError quietly, since that just means a newer request
      //    superseded this one (expected, not a failure worth showing).
      //  - surface everything else as a discount-code error so the shopper sees
      //    *something* went wrong and can retry, and log it for diagnostics.
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('[cart-discount] applyDiscount failed:', error);
        this.#handleDiscountError('discount_code');
      }
    } finally {
      this.#activeFetch = null;
      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
      }
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    // FIX (Micky cart audit): same busy-state treatment as applyDiscount above, so a
    // repeated tap on "remove" during the request can't silently cancel itself.
    pill.setAttribute('aria-busy', 'true');

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await withCartLock(() =>
        fetch(Theme.routes.cart_update_url, {
          ...config,
          signal: abortController.signal,
        })
      );

      if (!response.ok) {
        throw new Error(`Cart update request failed with status ${response.status}`);
      }

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
      // FIX (Micky cart audit): was an empty catch block. We intentionally do NOT
      // reuse the "discount code cannot be applied" error text here - that message
      // is about applying a code, not removing one, and showing it after a failed
      // removal would be misleading. We just log it and leave the pill in place so
      // the cart doesn't silently drop into an inconsistent state; the shopper can
      // simply try removing it again.
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('[cart-discount] removeDiscount failed:', error);
      }
    } finally {
      pill.removeAttribute('aria-busy');
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
